import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, DollarSign, Calendar, FileText } from 'lucide-react';
import { useInsurancePolicies } from '@/hooks/useInsurancePolicies';
import { calculateInsuranceMetrics } from '@/lib/insurance-utils';
import { Skeleton } from '@/components/ui/skeleton';

export function InsuranceMetricsCards() {
  const { data: policies, isLoading } = useInsurancePolicies();

  // For now, use USD as base currency with 1:1 exchange rates
  // TODO: Integrate with user profile settings for base currency
  const baseCurrency = 'USD';
  const exchangeRates: Record<string, number> = { USD: 1, EUR: 1.1, GBP: 1.3 };

  const metrics = policies 
    ? calculateInsuranceMetrics(policies, baseCurrency, exchangeRates)
    : null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: baseCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const cards = [
    {
      title: 'Total Coverage',
      value: metrics ? formatCurrency(metrics.totalCoverage) : '$0',
      icon: Shield,
      color: 'text-blue-600',
    },
    {
      title: 'Monthly Cost',
      value: metrics ? formatCurrency(metrics.monthlyPremiumCost) : '$0',
      icon: Calendar,
      color: 'text-green-600',
    },
    {
      title: 'Annual Cost',
      value: metrics ? formatCurrency(metrics.annualPremiumCost) : '$0',
      icon: DollarSign,
      color: 'text-purple-600',
    },
    {
      title: 'Active Policies',
      value: metrics ? metrics.activePolicyCount.toString() : '0',
      icon: FileText,
      color: 'text-orange-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className={`h-4 w-4 ${card.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{card.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
